import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["label", "children"];
import { iconSizes } from '@repay/cactus-icons';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';
import { margin, system } from 'styled-system';
import { isIE } from '../helpers/constants';
import { getOmittableProps } from '../helpers/omit';
import { borderSize } from '../helpers/theme';
var variantMap = {
  action: css(["color:", ";&:hover{color:", ";}"], function (p) {
    return p.theme.colors.callToAction;
  }, function (p) {
    return p.theme.colors.base;
  }),
  standard: css(["color:", ";&:hover{color:", ";}"], function (p) {
    return p.theme.colors.base;
  }, function (p) {
    return p.theme.colors.callToAction;
  }),
  danger: css(["color:", ";&:hover{color:", ";}"], function (p) {
    return p.theme.colors.error;
  }, function (p) {
    return p.theme.colors.errorDark;
  }),
  warning: css(["color:", ";&:hover{color:", ";}"], function (p) {
    return p.theme.colors.warningDark;
  }, function (p) {
    return p.theme.colors.warning;
  }),
  success: css(["color:", ";&:hover{color:", ";}"], function (p) {
    return p.theme.colors.success;
  }, function (p) {
    return p.theme.colors.successDark;
  }),
  dark: css(["color:", ";&:hover{color:", ";}"], function (p) {
    return p.theme.colors.darkContrast;
  }, function (p) {
    return p.theme.colors.callToAction;
  })
};
var inverseVariantMap = {
  action: css(["color:", ";&:hover,&:focus{color:", ";}"], function (p) {
    return p.theme.colors.callToAction;
  }, function (p) {
    return p.theme.colors.white;
  }),
  standard: css(["color:", ";&:hover,&:focus{color:", ";background:", ";}"], function (p) {
    return p.theme.colors.white;
  }, function (p) {
    return p.theme.colors.base;
  }, function (p) {
    return p.theme.colors.white;
  }),
  danger: css(["color:", ";&:hover,&:focus{color:", ";}"], function (p) {
    return p.theme.colors.error;
  }, function (p) {
    return p.theme.colors.white;
  }),
  warning: css(["color:", ";&:hover{color:", ";}"], function (p) {
    return p.theme.colors.warningDark;
  }, function (p) {
    return p.theme.colors.warning;
  }),
  success: css(["color:", ";&:hover{color:", ";}"], function (p) {
    return p.theme.colors.success;
  }, function (p) {
    return p.theme.colors.successDark;
  }),
  dark: css(["color:", ";&:hover{color:", ";}"], function (p) {
    return p.theme.colors.darkContrast;
  }, function (p) {
    return p.theme.colors.callToAction;
  })
};
var disabled = /*#__PURE__*/css(["color:", ";cursor:not-allowed;"], function (p) {
  return p.theme.colors.mediumGray;
});
var shapeMap = {
  square: 'border-radius: 1px;',
  intermediate: 'border-radius: 8px;',
  round: 'border-radius: 50%;'
};
var focusOutlineSpacing = {
  tiny: 4,
  small: 8,
  medium: 8,
  large: 12
};

var variantOrDisabled = function variantOrDisabled(props) {
  var map = props.inverse ? inverseVariantMap : variantMap;

  if (props.disabled) {
    return disabled;
  } else if (props.variant !== undefined) {
    return map[props.variant];
  }
};

var IconButtonBase = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var label = props.label,
      children = props.children,
      buttonProps = _objectWithoutProperties(props, _excluded);

  return /*#__PURE__*/React.createElement("button", Object.assign({
    "aria-label": label,
    ref: ref
  }, buttonProps), children);
});
var focusOutline = system({
  iconSize: function iconSize(value) {
    var offset = focusOutlineSpacing[value] || focusOutlineSpacing.medium;
    var styles = {
      height: "calc(100% + ".concat(offset, "px)"),
      width: "calc(100% + ".concat(offset, "px)")
    };

    if (isIE) {
      var ieOffset = offset / 2;
      styles.top = "-".concat(ieOffset + 1, "px");
      styles.left = "-".concat(ieOffset, "px");
    }

    return styles;
  }
});
var styleProps = getOmittableProps(margin, 'display', 'inverse', 'variant', 'iconSize');
export var IconButton = /*#__PURE__*/styled(IconButtonBase).withConfig({
  shouldForwardProp: function shouldForwardProp(p) {
    return !styleProps.has(p);
  }
}).withConfig({
  displayName: "IconButton"
})(["display:", ";box-sizing:border-box;align-items:center;justify-content:center;padding:1px;border:none;background:transparent;outline:none;cursor:pointer;position:relative;overflow:visible;&::-moz-focus-inner{border:0;}&:focus{::after{content:'';display:block;position:absolute;", " ", " ", " border-color:", ";box-sizing:border-box;}}", " ", " ", ""], function (p) {
  return p.display || 'inline-flex';
}, focusOutline, function (p) {
  return "border: ".concat(borderSize(p), " solid;");
}, function (p) {
  return shapeMap[p.theme.shape];
}, function (p) {
  return p.theme.colors.callToAction;
}, variantOrDisabled, margin, iconSizes);
IconButton.propTypes = {
  iconSize: PropTypes.oneOf(['tiny', 'small', 'medium', 'large']),
  variant: PropTypes.oneOf(['standard', 'action', 'danger', 'warning', 'success', 'dark']),
  disabled: PropTypes.bool,
  label: function label(props, propName, componentName) {
    if (!props.label && !props['aria-label'] && !props['aria-labelledby']) {
      return new Error("One of props 'label' or 'aria-labelledby' was not specified in ".concat(componentName, "."));
    } else if (props.label !== undefined && typeof props.label !== 'string') {
      return new Error("Invalid prop 'label' of type '".concat(typeof props.label, "' supplied to '").concat(componentName, "', expected 'string'."));
    }

    return null;
  },
  'aria-labelledby': PropTypes.string,
  display: PropTypes.oneOf(['flex', 'inline-flex']),
  inverse: PropTypes.bool
};
IconButton.defaultProps = {
  variant: 'standard',
  iconSize: 'medium',
  display: 'inline-flex',
  type: 'button'
};
export default IconButton;